/* eslint-disable react/forbid-dom-props */
import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Download, Upload, CheckCircle, RefreshCw,
  FileText, Building2, MapPin, Clock, ChevronLeft, Sparkles, AlertTriangle,
  ShieldCheck, HelpCircle, Calculator,
} from 'lucide-react';
import { casesApi, petitionsApi } from '../../services/api';
import { triggerBlobDownload } from '../../utils/download';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DocumentPreview from '../../components/DocumentPreview';
import QualityPanel from '../../components/QualityPanel';
import CalculoPanel from '../../components/CalculoPanel';
import PerguntasPanel from '../../components/PerguntasPanel';
import { Modal } from '../../components/ui';

const REVIEW_STATUSES = ['aguardando_revisao', 'em_revisao'];

// Abas da tela, na ordem do trabalho do revisor: a peça primeiro — é o que ele veio ler, e em
// largura cheia, não espremida numa coluna ao lado do painel —, depois o que a máquina apontou
// (qualidade), o que só o cliente responde (pendências), a conta (cálculo), o envio do arquivo
// revisado e por fim o histórico.
const ABAS_REVISAO = [
  { id: 'peca',       label: 'Peça',       Icon: FileText,    descricao: 'O rascunho gerado pela IA, ou o diff contra a versão revisada' },
  { id: 'qualidade',  label: 'Qualidade',  Icon: ShieldCheck, descricao: 'Crítica da revisão e quality gate da versão gerada' },
  { id: 'pendencias', label: 'Pendências', Icon: HelpCircle,  descricao: 'Perguntas que destravam a geração; a resposta é gravada na ficha' },
  { id: 'calculo',    label: 'Cálculo',    Icon: Calculator,  descricao: 'Memória de cálculo dos pedidos e campos que faltam' },
  { id: 'enviar',     label: 'Enviar',     Icon: Upload,      descricao: 'Upload do .docx revisado e motivo das alterações' },
  { id: 'historico',  label: 'Histórico',  Icon: Clock,       descricao: 'Versões anteriores desta petição' },
];

function buildDiffHtmlFromParts(diffJson) {
  if (!diffJson || !diffJson.parts) return { html: '', added: 0, removed: 0, secoes: [] };

  const html = diffJson.parts.map(({ op, text }) => {
    const escaped = (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>');

    if (op === 'insert') {
      return `<ins style="background:#d4edda;color:#2d6a4f;text-decoration:none;"> ${escaped}</ins>`;
    }
    if (op === 'delete') {
      return `<del style="background:#fde8e8;color:#c0392b;text-decoration:line-through;"> ${escaped}</del>`;
    }
    return ` ${escaped}`;
  }).join('');

  return {
    html,
    added: diffJson.words_added || 0,
    removed: diffJson.words_removed || 0,
    secoes: diffJson.secoes || [],
  };
}

function CriticaPanel({ critica, qualityReport }) {
  const hasCritica = Array.isArray(critica) && critica.length > 0;
  const failedChecks = Array.isArray(qualityReport?.checks)
    ? qualityReport.checks.filter(c => c.status === 'failed')
    : [];

  if (!hasCritica && failedChecks.length === 0) return null;

  return (
    <div className="card" style={{ borderLeft: '3px solid var(--warning)' }}>
      <div className="card-header">
        <AlertTriangle size={15} style={{ color: '#875708' }} />
        <span className="card-title">Crítica da Revisão</span>
      </div>

      {hasCritica && (
        <div style={{ marginBottom: failedChecks.length ? '.75rem' : 0 }}>
          <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '.35rem' }}>
            Observações automáticas da IA:
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '.75rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {critica.map((item, idx) => (
              <li key={idx} style={{ marginBottom: '.25rem' }}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      {failedChecks.length > 0 && (
        <div>
          <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--error)', marginBottom: '.35rem' }}>
            Pendências no Quality Gate da Revisão:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
            {failedChecks.map(check => (
              <div key={check.id} style={{ fontSize: '.73rem', background: 'var(--bg)', padding: '.35rem .5rem', borderRadius: 'var(--radius-sm)' }}>
                <span style={{ fontWeight: 600, color: check.severity === 'critical' ? 'var(--error)' : 'var(--warning)' }}>
                  {check.label}:
                </span>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>{check.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ReviewMode({ caso, onBack, onSent }) {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [reason, setReason] = useState('');
  const [diff, setDiff] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagsIgnoradas, setTagsIgnoradas] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submittingApproval, setSubmittingApproval] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [promptComplementar, setPromptComplementar] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [secoesAlterar, setSecoesAlterar] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const { data: petition, isLoading: loadingPetition } = useQuery({
    queryKey: ['petition', caso.case_id],
    queryFn: () => petitionsApi.getByCaseId(caso.case_id),
    enabled: !!caso.case_id,
  });

  const activePetitionId = caso.petition_id || petition?.id;
  const activePetitionVersion = caso.petition_version || petition?.version || 1;

  const { data: currentRevision } = useQuery({
    queryKey: ['petition-revision', activePetitionId],
    queryFn: () => petitionsApi.getRevision(activePetitionId),
    enabled: !!activePetitionId && (caso.status === 'em_revisao' || caso.status === 'aguardando_revisao'),
  });

  const restoredRevisionId = useRef(null);
  useEffect(() => {
    // Query v5 não executa onSuccess. Restaurar uma vez evita apagar edições locais no refetch.
    if (!currentRevision || restoredRevisionId.current === currentRevision.id) return;
    restoredRevisionId.current = currentRevision.id;
    setDiff(buildDiffHtmlFromParts(currentRevision.diff_json));
    setReason(currentRevision.reviewer_notes || '');
    setTags(currentRevision.tags || []);
    setTagsIgnoradas(currentRevision.tags_ignoradas || []);
  }, [currentRevision]);

  const { data: versions = [], refetch: refetchVersions } = useQuery({
    queryKey: ['petition-versions', caso.case_id],
    queryFn: () => petitionsApi.getVersions(caso.case_id),
    enabled: !!caso.case_id,
  });

  // Mesmas `queryKey` dos painéis: o React Query desduplica, então os contadores das abas não
  // custam requisição extra. Sem eles o revisor teria de abrir cada aba para saber se há algo.
  const { data: perguntasData } = useQuery({
    queryKey: ['perguntas-caso', caso.case_id],
    queryFn: () => casesApi.getPerguntas(caso.case_id),
    enabled: !!caso.case_id,
  });
  const { data: calculoData } = useQuery({
    queryKey: ['calculo-pedidos', caso.case_id],
    queryFn: () => petitionsApi.getCalculo(caso.case_id),
    enabled: !!caso.case_id,
  });

  const criticaItens = Array.isArray(currentRevision?.critica_json) ? currentRevision.critica_json.length : 0;
  const checksRevisao = (currentRevision?.quality_report?.checks || []).filter(c => c.status === 'failed');
  const checksGeracao = (petition?.quality_report?.checks || []).filter(c => c.status === 'failed');
  const qualidadeItens = criticaItens + checksRevisao.length + checksGeracao.length;
  const temCritico = [...checksRevisao, ...checksGeracao].some(c => c.severity === 'critical');

  const perguntasItens = (perguntasData?.perguntas || []).length;
  const pendenciasCalculo = (calculoData?.pendencias || []).length;
  const pedidosCalculo = (calculoData?.pedidos || []).length;

  const temQualidade = qualidadeItens > 0;
  const temCalculo = pendenciasCalculo + pedidosCalculo > 0;

  const contagens = {
    peca:       { valor: 0, alerta: false },
    qualidade:  { valor: qualidadeItens, alerta: temCritico },
    pendencias: { valor: perguntasItens, alerta: perguntasItens > 0 },
    calculo:    { valor: pendenciasCalculo, alerta: pendenciasCalculo > 0 },
    enviar:     { valor: file ? 1 : 0, alerta: false },
    historico:  { valor: versions.length, alerta: false },
  };

  // Abre na peça: é o que o revisor veio ler. O trabalho pendente não some — aparece no contador
  // das outras abas, que é justamente para isso.
  const [abaEscolhida, setAbaEscolhida] = useState(null);
  const abaSugerida = 'peca';
  const abaAtiva = abaEscolhida || abaSugerida;
  const setAbaAtiva = setAbaEscolhida;

  function selectFile(selected) {
    if (!selected) return;
    setFile(selected);
    setDiff(null);
    setTags([]);
    setTagsIgnoradas([]);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    selectFile(e.dataTransfer.files?.[0]);
  }

  async function handleDownload(format = 'docx') {
    if (!activePetitionId) {
      toast.error('Petição ainda não encontrada para este caso.');
      return;
    }
    try {
      const blob = await petitionsApi.download(activePetitionId, format);
      const ext = format === 'md' ? 'md' : 'docx';
      const cliente = (caso.client_name || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      triggerBlobDownload(blob, `peticao_v${activePetitionVersion}_${cliente}.${ext}`);
      toast.success('Download iniciado');
    } catch {
      toast.error('Erro ao baixar a petição');
    }
  }

  async function handleDownloadVersion(vId, versionNum, format = 'docx') {
    try {
      const blob = await petitionsApi.download(vId, format);
      const ext = format === 'md' ? 'md' : 'docx';
      const cliente = (caso.client_name || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '_');
      triggerBlobDownload(blob, `peticao_v${versionNum}_${cliente}.${ext}`);
      toast.success('Download iniciado');
    } catch {
      toast.error('Erro ao baixar a versão selecionada');
    }
  }

  async function handleSendRevision() {
    if (!file || !reason.trim()) return;
    if (!activePetitionId) {
      toast.error('Petição ainda não encontrada para este caso.');
      return;
    }
    setUploading(true);
    try {
      const result = await petitionsApi.uploadRevision(activePetitionId, file, reason.trim());
      setDiff(buildDiffHtmlFromParts(result.diff_json));
      setTags(result.tags || []);
      setTagsIgnoradas(result.tags_ignoradas || []);
      toast.success('Revisão salva em minuta! Você pode continuar ajustando ou enviar para aprovação.');
      queryClient.invalidateQueries({ queryKey: ['cases-review'] });
      refetchVersions();
    } catch {
      toast.error('Erro ao enviar a revisão');
    } finally {
      setUploading(false);
    }
  }

  async function handleSendToApproval() {
    if (!activePetitionId) return;
    setSubmittingApproval(true);
    try {
      await petitionsApi.submitApproval(activePetitionId);
      toast.success('Petição enviada para aprovação!');
      queryClient.invalidateQueries({ queryKey: ['cases-review'] });
      onSent(caso.case_id);
    } catch {
      toast.error('Erro ao enviar para aprovação.');
    } finally {
      setSubmittingApproval(false);
    }
  }

  async function handleSaveNote() {
    if (!activePetitionId || !reason.trim()) return;
    setSavingNote(true);
    try {
      await petitionsApi.saveReviewNote(activePetitionId, reason.trim());
      await refetchVersions();
      toast.success('Consideração preservada no histórico deste caso.');
    } catch {
      toast.error('Não foi possível salvar. O texto continua disponível para tentar novamente.');
    } finally {
      setSavingNote(false);
    }
  }

  async function handleRetryLearning(petitionId, revisionId) {
    try {
      await petitionsApi.retryLearning(petitionId, revisionId);
      await refetchVersions();
      queryClient.invalidateQueries({ queryKey: ['petition-revision', activePetitionId] });
      toast.success('Nova tentativa de análise solicitada; acompanhe o estado no histórico.');
    } catch {
      toast.error('Não foi possível iniciar outra análise. Confira se há uma tentativa em andamento.');
    }
  }

  function openRegenerateModal() {
    const alteradas = (diff?.secoes || []).filter(s => s.words_added + s.words_removed > 0).map(s => s.secao_id);
    setSecoesAlterar(alteradas.length ? alteradas : null);
    if (!promptComplementar) {
      const suggestions = [];
      if (diff?.secoes && diff.secoes.length > 0) {
        const sortedSec = [...diff.secoes].sort((a, b) => (b.words_added + b.words_removed) - (a.words_added + a.words_removed));
        const topSec = sortedSec.slice(0, 3).map(s => `${s.secao_id} (+${s.words_added}/-${s.words_removed})`).join(', ');
        suggestions.push(`Ajustar redação priorizando as seções mais modificadas: ${topSec}.`);
      }
      if (currentRevision?.critica_json && currentRevision.critica_json.length > 0) {
        suggestions.push(`Pontos de atenção identificados:\n- ${currentRevision.critica_json.join('\n- ')}`);
      }
      if (reason.trim()) {
        suggestions.push(`Observação do revisor: ${reason.trim()}`);
      }
      setPromptComplementar(suggestions.join('\n\n'));
    }
    setIsRegenerateModalOpen(true);
  }

  async function handleRegenerateFromRevision() {
    if (!activePetitionId) return;
    setRegenerating(true);
    try {
      await petitionsApi.regenerateFromRevision(activePetitionId, {
        prompt_complementar: promptComplementar.trim() || undefined,
        secoes: secoesAlterar || undefined,
      });
      toast.success('Regeração disparada a partir da revisão atual!');
      setIsRegenerateModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['cases-review'] });
      onSent(caso.case_id);
    } catch {
      toast.error('Erro ao disparar regeração da petição.');
    } finally {
      setRegenerating(false);
    }
  }

  async function handleRequestNew() {
    if (!activePetitionId) {
      toast.error('Petição ainda não encontrada para este caso.');
      return;
    }
    setRejecting(true);
    try {
      await petitionsApi.reject(activePetitionId, reason.trim() || undefined);
      toast.success('Nova versão IA solicitada. Caso retornado para a ficha.');
      onSent(caso.case_id);
    } catch {
      toast.error('Erro ao solicitar nova versão');
    } finally {
      setRejecting(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <ChevronLeft size={14} /> Voltar
        </button>
        <div>
          <h1 style={{ fontSize: '1.375rem' }}>Revisão de Petição</h1>
          <p style={{ marginTop: '.1rem', fontSize: '.85rem' }}>
            {caso.client_name} · {caso.defendant_name}
          </p>
        </div>
        {caso.petition_version && (
          <span className="badge badge-navy" style={{ marginLeft: 'auto' }}>
            Versão {caso.petition_version}
          </span>
        )}
      </div>

      {/* Estado da peça: vale para a tela inteira, não só para a aba do documento. */}
      <div className="revisao-estado" style={{
        background: caso.status === 'aprovada' || caso.status === 'aguardando_protocolo' || caso.status === 'protocolado'
          ? 'var(--success-bg)'
          : caso.status === 'aguardando_aprovacao'
          ? 'var(--warning-bg)'
          : 'var(--info-bg)',
        color: caso.status === 'aprovada' || caso.status === 'aguardando_protocolo' || caso.status === 'protocolado'
          ? 'var(--success)'
          : caso.status === 'aguardando_aprovacao'
          ? 'var(--warning-badge-text)'
          : 'var(--info)',
      }}>
        <span>Estado: {
          caso.status === 'aprovada' || caso.status === 'aguardando_protocolo' ? 'APROVADA (Pronta para protocolo)' :
          caso.status === 'protocolado' ? 'PROTOCOLADA' :
          caso.status === 'aguardando_aprovacao' ? 'AGUARDANDO APROVAÇÃO' :
          'EM REVISÃO (Minuta)'
        }</span>
        <span style={{ fontSize: '.75rem', opacity: 0.8 }}>v{activePetitionVersion}</span>
      </div>

      <div className="revisao-painel">
          <div className="revisao-abas" role="tablist" aria-label="Painéis da revisão">
            {ABAS_REVISAO.map(aba => {
              const contagem = contagens[aba.id];
              const ativa = abaAtiva === aba.id;
              return (
                <button
                  key={aba.id}
                  type="button"
                  role="tab"
                  id={`aba-${aba.id}`}
                  aria-selected={ativa}
                  aria-controls={`painel-${aba.id}`}
                  onClick={() => setAbaAtiva(aba.id)}
                  title={aba.descricao}
                >
                  {/* O ícone some abaixo de ~420px de painel: cinco abas com ícone, rótulo e
                      contador não cabem em 360px e as duas últimas ficavam fora da vista. */}
                  <aba.Icon size={13} className="revisao-aba-icone" />
                  {aba.label}
                  {contagem.valor > 0 && (
                    <span className={`revisao-aba-contagem${contagem.alerta ? ' revisao-aba-alerta' : ''}`}>
                      {contagem.valor}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div
            className="revisao-conteudo revisao-conteudo-amplo"
            role="tabpanel"
            id={`painel-${abaAtiva}`}
            aria-labelledby={`aba-${abaAtiva}`}
          >
            {abaAtiva === 'peca' && (
              <>
            <div style={{ padding: '.875rem 1.375rem', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '.5rem' }}>
              <span className="card-title">
                {diff ? 'Diff: Original × Revisado' : 'Rascunho gerado pela IA'}
              </span>
              <div style={{ display: 'flex', gap: '.375rem' }}>
                <button className="btn btn-outline btn-sm" onClick={() => handleDownload('docx')}>
                  <Download size={13} /> .docx
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => handleDownload('md')}>
                  <Download size={13} /> .md
                </button>
              </div>
            </div>

            {diff ? (
              <div>
                <div style={{ padding: '.5rem 1.375rem', background: 'var(--bg)', borderBottom: '1px solid var(--border-light)', display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '.775rem', color: 'var(--success)', fontWeight: 500 }}>
                    +{diff.added} palavras adicionadas
                  </span>
                  <span style={{ fontSize: '.775rem', color: 'var(--error)', fontWeight: 500 }}>
                    −{diff.removed} palavras removidas
                  </span>
                </div>
                {diff.secoes && diff.secoes.length > 0 && (
                  <div style={{ padding: '.5rem 1.375rem', background: 'var(--bg-hover)', borderBottom: '1px solid var(--border-light)', display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
                    {diff.secoes.map(s => (
                      <span key={s.secao_id} className="badge" style={{ fontSize: '.68rem', background: '#fff', border: '1px solid var(--border)', padding: '.15rem .35rem' }}>
                        <strong>{s.secao_id}</strong>: +{s.words_added} −{s.words_removed}
                      </span>
                    ))}
                  </div>
                )}
                {tags.length > 0 && (
                  /* As tags saem do documento na ingestão — o revisor precisa ver que foram lidas. */
                  <div style={{ padding: '.75rem 1.375rem', background: 'var(--bg)', borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ fontSize: '.775rem', fontWeight: 500, marginBottom: '.5rem' }}>
                      {tags.length} instrução(ões) lida(s) e removida(s) do documento
                    </div>
                    {tags.map((tag, i) => (
                      <div key={i} style={{ fontSize: '.75rem', lineHeight: 1.6, marginBottom: '.4rem' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)' }}>
                          [{tag.prefixo}]
                        </span>{' '}
                        <span style={{ color: 'var(--text-muted)' }}>
                          {tag.secao || 'fora de seção'}
                          {tag.prefixo === 'ia' ? ' · vale só neste caso' : ' · aguarda curadoria'}
                        </span>
                        <div>{tag.instrucao}</div>
                        {tag.ancora && (
                          <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                            em: {tag.ancora.slice(0, 120)}{tag.ancora.length > 120 ? '…' : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {tagsIgnoradas.length > 0 && (
                  /* Não viram lição: o que a anotação quis dizer seria adivinhação. Mas o revisor
                     precisa saber, porque a de colchete fechado saiu da peça. */
                  <div style={{ padding: '.75rem 1.375rem', background: 'var(--bg)', borderBottom: '1px solid var(--border-light)' }}>
                    <div style={{ fontSize: '.775rem', fontWeight: 500, marginBottom: '.4rem', color: 'var(--error)' }}>
                      {tagsIgnoradas.length} anotação(ões) não compreendida(s) — use [ia: ...] ou [regra: ...]
                    </div>
                    {tagsIgnoradas.map((t, i) => (
                      <div key={i} style={{ fontSize: '.75rem', lineHeight: 1.6 }}>
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{t.texto}</span>{' '}
                        <span style={{ color: 'var(--text-muted)' }}>
                          {t.removida ? '· removida da peça' : '· ficou na peça, falta fechar o colchete'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  style={{ padding: '1.375rem', fontFamily: 'var(--font-mono)', fontSize: '.78rem', lineHeight: 1.75, maxHeight: 520, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  dangerouslySetInnerHTML={{ __html: diff.html }}
                />
              </div>
            ) : loadingPetition ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                <div className="spinner" style={{ width: 28, height: 28 }} />
              </div>
            ) : petition?.content_json ? (
              <div style={{ padding: '1rem', maxHeight: 520, overflowY: 'auto' }}>
                {petition.content_json.origem === 'docx_revisado' && (
                  <p className="revisao-aviso-upload">
                    Esta versão é o .docx enviado pelo revisor. A peça é o arquivo (baixe-o acima);
                    a prévia abaixo é uma leitura do texto e pode não mostrar tabelas e formatação.
                  </p>
                )}
                <DocumentPreview content={petition.content_json} />
              </div>
            ) : (
              <div style={{ padding: '2rem 1.375rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <FileText size={28} style={{ opacity: .3, marginBottom: '.75rem' }} />
                <p style={{ fontSize: '.85rem' }}>
                  Baixe o .docx gerado pela IA, revise no Word e suba a versão revisada na aba <strong>Enviar</strong>.
                </p>
              </div>
            )}
              </>
            )}

            {abaAtiva === 'qualidade' && (
              temQualidade ? (
                <>
                  <CriticaPanel critica={currentRevision?.critica_json} qualityReport={currentRevision?.quality_report} />
                  <QualityPanel report={petition?.quality_report} />
                </>
              ) : (
                <div className="revisao-vazio">
                  <ShieldCheck size={26} style={{ opacity: .3 }} />
                  <span>Nenhuma pendência de qualidade nesta versão.</span>
                </div>
              )
            )}

            {abaAtiva === 'pendencias' && (
              // Com tudo dispensado o painel ainda aparece: é nele que se reabre uma dispensa.
              contagens.pendencias.valor > 0 || (perguntasData?.dispensadas || []).length > 0 ? (
                <PerguntasPanel caseId={caso.case_id} />
              ) : (
                <div className="revisao-vazio">
                  <CheckCircle size={26} style={{ opacity: .3 }} />
                  <span>Nada a responder. Tudo que travava a geração já foi resolvido na ficha.</span>
                </div>
              )
            )}

            {abaAtiva === 'calculo' && (
              temCalculo ? (
                <CalculoPanel caseId={caso.case_id} />
              ) : (
                <div className="revisao-vazio">
                  <FileText size={26} style={{ opacity: .3 }} />
                  <span>Sem memória de cálculo para esta ficha.</span>
                </div>
              )
            )}

            {abaAtiva === 'enviar' && (
              <>
                <div className="card">
                  <div className="card-header">
                    <Upload size={15} style={{ color: 'var(--gold)' }} />
                    <span className="card-title">Upload do revisado</span>
                  </div>
                  <div
                    style={{
                      border: `2px dashed ${dragging ? 'var(--gold)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-md)', padding: '1.25rem',
                      textAlign: 'center', background: dragging ? 'var(--gold-pale)' : 'var(--bg)',
                      transition: 'all .15s', cursor: 'pointer',
                    }}
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={20} style={{ color: 'var(--text-muted)', marginBottom: '.4rem' }} />
                    <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: '.25rem' }}>
                      {file ? file.name : 'Arraste o .docx revisado ou clique'}
                    </p>
                    <span className="badge badge-navy" style={{ fontSize: '.68rem' }}>DOCX</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".docx"
                      style={{ display: 'none' }}
                      onChange={e => selectFile(e.target.files?.[0])}
                    />
                  </div>
                  {file && !diff && (
                    <div className="alert alert-success" style={{ marginTop: '.75rem' }}>
                      <CheckCircle size={14} /> Arquivo selecionado — preencha o motivo e envie
                    </div>
                  )}
                </div>

                <div className="card">
                  <div className="card-header">
                    <FileText size={15} style={{ color: 'var(--gold)' }} />
                    <span className="card-title">Motivo das alterações</span>
                    <span className="req" style={{ marginLeft: 'auto' }}>*obrigatório</span>
                  </div>
                  <textarea
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    rows={5}
                    placeholder="Descreva as principais alterações realizadas..."
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </>
            )}

            {abaAtiva === 'historico' && (
              versions.length === 0 ? (
                <div className="revisao-vazio">
                  <Clock size={26} style={{ opacity: .3 }} />
                  <span>Nenhuma outra versão disponível.</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                  {versions.map(v => (
                    <div key={v.id} style={{ fontSize: '.775rem', padding: '.4rem 0', borderBottom: '1px solid var(--border-light)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.5rem' }}>
                        <span>
                          <strong>v{v.version}</strong> ({v.status === 'aprovada' ? 'Aprovada' : v.status === 'aguardando_aprovacao' ? 'Aprov. pendente' : 'Em revisão'})
                        </span>
                        <div style={{ display: 'flex', gap: '.25rem' }}>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '0 .25rem', height: 'auto', fontSize: '.725rem' }} onClick={() => handleDownloadVersion(v.id, v.version, 'docx')}>
                            .docx
                          </button>
                          <button className="btn btn-ghost btn-sm" style={{ padding: '0 .25rem', height: 'auto', fontSize: '.725rem' }} onClick={() => handleDownloadVersion(v.id, v.version, 'md')}>
                            .md
                          </button>
                        </div>
                      </div>
                      {(v.revisoes || []).map(revisao => (
                        <details key={revisao.id} className="revisao-historico-notas">
                          <summary>Considerações salvas da revisão · {new Date(revisao.created_at).toLocaleString('pt-BR')}</summary>
                          {revisao.kind === 'documento' && <p>Análise automática: {revisao.extraction_status === 'completed' ? 'concluída' : revisao.extraction_status === 'failed' ? 'falhou; considerações preservadas' : revisao.extraction_status === 'unknown' ? 'estado antigo não confirmado' : 'em processamento'}.</p>}
                          {revisao.extraction_error && <p>{revisao.extraction_error}</p>}
                          {revisao.kind === 'documento' && ['failed', 'unknown', 'running', 'queued'].includes(revisao.extraction_status) && <button className="btn btn-ghost btn-sm" onClick={() => handleRetryLearning(v.id, revisao.id)}>Tentar análise novamente</button>}
                          <p className="revisao-texto-preservado">{revisao.reviewer_notes || 'Sem observação escrita.'}</p>
                          {revisao.critica_json?.length > 0 ? (
                            <><strong>Considerações da IA</strong><ul>{revisao.critica_json.map((item, index) => <li key={index}>{item}</li>)}</ul></>
                          ) : <p>Sem crítica automática registrada. Isso não confirma ausência de problemas.</p>}
                        </details>
                      ))}
                      {v.prompt_complementar && (
                        <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: '.2rem', fontStyle: 'italic' }} title={v.prompt_complementar}>
                          Instrução: {v.prompt_complementar}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          {/* Ações: fora das abas de propósito — salvar e enviar para aprovação não podem
              depender de qual aba está aberta. */}
          <div className="revisao-acoes">
            <button className="btn btn-secondary" disabled={!reason.trim() || !activePetitionId || savingNote} onClick={handleSaveNote}>
              {savingNote ? 'Salvando...' : 'Salvar consideração no histórico'}
            </button>
            <button
              className="btn btn-primary"
              style={{ background: 'var(--success)', borderColor: 'var(--success)' }}
              onClick={handleSendRevision}
              disabled={!activePetitionId || !file || !reason.trim() || uploading}
              title={!file ? 'Faça o upload na aba Enviar' : !reason.trim() ? 'Preencha o motivo das alterações na aba Enviar' : ''}
            >
              <Upload size={14} /> {uploading ? 'Salvando...' : 'Salvar Revisão'}
            </button>

            {(caso.status === 'em_revisao' || diff) && (
              <button
                className="btn btn-outline"
                style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}
                onClick={openRegenerateModal}
                disabled={!activePetitionId || regenerating}
              >
                <Sparkles size={14} /> {regenerating ? 'Regerando...' : 'Regerar com IA'}
              </button>
            )}

            {(caso.status === 'em_revisao' || diff) && (
              <button
                className="btn btn-gold"
                onClick={handleSendToApproval}
                disabled={!activePetitionId || submittingApproval}
              >
                <CheckCircle size={14} /> {submittingApproval ? 'Enviando...' : 'Enviar para Aprovação'}
              </button>
            )}

            <button className="btn btn-outline" onClick={handleRequestNew} disabled={!activePetitionId || rejecting}>
              <RefreshCw size={14} /> {rejecting ? 'Solicitando...' : 'Solicitar Nova Versão IA (Ficha)'}
            </button>
          </div>
        </div>

      <Modal
        isOpen={isRegenerateModalOpen}
        onClose={() => setIsRegenerateModalOpen(false)}
        title="Regerar com a IA a partir desta revisão"
        width={600}
        footer={
          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', width: '100%' }}>
            <button className="btn btn-ghost" onClick={() => setIsRegenerateModalOpen(false)} disabled={regenerating}>
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleRegenerateFromRevision} disabled={regenerating || (secoesAlterar !== null && secoesAlterar.length === 0)}>
              <Sparkles size={14} /> {regenerating ? 'Disparando...' : 'Disparar Regeração'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          <p style={{ fontSize: '.825rem', color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
            As seções desmarcadas serão preservadas. A tabela de pedidos e os valores serão reconciliados com a ficha atual. Sem seleção específica, será feita uma nova redação integral.
          </p>
          {(diff?.secoes || []).length > 0 && <fieldset className="revisao-secoes-selecao">
            <legend>Seções a reescrever</legend>
            <label><input type="checkbox" checked={secoesAlterar === null} onChange={e => setSecoesAlterar(e.target.checked ? null : [])} /> Reescrever todas</label>
            {(diff.secoes || []).map(secao => <label key={secao.secao_id}>
              <input type="checkbox" checked={secoesAlterar === null || secoesAlterar.includes(secao.secao_id)} disabled={secoesAlterar === null}
                onChange={e => setSecoesAlterar(prev => e.target.checked ? [...prev, secao.secao_id] : prev.filter(id => id !== secao.secao_id))} />
              {secao.secao_id.replaceAll('_', ' ')}
            </label>)}
          </fieldset>}
          <div>
            <label style={{ display: 'block', fontSize: '.775rem', fontWeight: 600, marginBottom: '.35rem' }}>
              Instruções complementares para o Redator (opcional):
            </label>
            <textarea
              value={promptComplementar}
              onChange={e => setPromptComplementar(e.target.value)}
              rows={6}
              placeholder="Instruções específicas para esta regeração..."
              style={{ width: '100%', resize: 'vertical', fontSize: '.8rem' }}
            />
            <span style={{ fontSize: '.725rem', color: 'var(--text-muted)' }}>
              Estas instruções valerão apenas para a nova versão gerada.
            </span>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default function Revisao() {
  const { data: allCases = [], isLoading } = useQuery({
    queryKey: ['cases-review'],
    queryFn: casesApi.list,
    refetchInterval: 30000,
  });
  const location = useLocation();
  const [selectedCaseId, setSelectedCaseId] = useState(() => {
    return location.state?.caseId || null;
  });

  const cases = allCases.filter(c => REVIEW_STATUSES.includes(c.status));
  const selectedCase = cases.find(c => c.case_id === selectedCaseId) || null;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <div className="spinner" style={{ width: 28, height: 28 }} />
      </div>
    );
  }

  if (selectedCase) {
    return (
      <ReviewMode
        caso={selectedCase}
        onBack={() => setSelectedCaseId(null)}
        onSent={() => setSelectedCaseId(null)}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1>Revisão de Petições</h1>
          <p style={{ marginTop: '.1rem' }}>
            {cases.length} {cases.length === 1 ? 'petição aguardando' : 'petições aguardando'} revisão
          </p>
        </div>
      </div>

      {cases.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: '.875rem', color: 'var(--text-muted)' }}>
          <CheckCircle size={44} style={{ opacity: .25 }} />
          <p>Nenhuma petição aguardando revisão</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          {cases.map(c => (
            <div
              key={c.case_id}
              className="card"
              style={{ cursor: 'pointer', transition: 'box-shadow .13s' }}
              onClick={() => setSelectedCaseId(c.case_id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.625rem', marginBottom: '.375rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)' }}>
                      {c.client_name}
                    </span>
                    <span className={`badge ${c.status === 'em_revisao' ? 'badge-warning' : 'badge-gold'}`}>
                      {c.status === 'em_revisao' ? 'Em revisão' : 'Aguardando revisão'}
                    </span>
                    {c.petition_version && (
                      <span className="badge badge-navy">v{c.petition_version}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '.8rem', color: 'var(--text-muted)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                      <Building2 size={12} /> {c.defendant_name}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                      <MapPin size={12} /> {c.comarca}
                    </span>
                    {c.updated_at && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }}>
                        <Clock size={12} />
                        {formatDistanceToNow(new Date(c.updated_at), { addSuffix: true, locale: ptBR })}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  style={{ marginLeft: '1.25rem', flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); setSelectedCaseId(c.case_id); }}
                >
                  Revisar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {cases.length > 0 && (
        <div style={{ marginTop: '1.25rem', padding: '.75rem 1rem', background: 'var(--info-bg)', borderRadius: 'var(--radius-md)', fontSize: '.8rem', color: 'var(--info)' }}>
          Dica: clique em um caso ou em "Revisar" para abrir o modo de revisão com diff visual.
        </div>
      )}
    </div>
  );
}
